import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useTheme } from '@material-ui/core/styles'
import AddCommentOutlinedIcon from 'components/material-symbol/icons/AddComment'
import Rating from 'material-ui-rating'
import CoachReviewCard from './coach-review-card'
import useStyles from './coach-reviews.styles'
import { coachReviewCardRatingIcons } from './rating-icons'
import { secureLink, useIntersectionObserver } from 'lib/utils'

const REVIEWS_BATCH_SIZE = 4

const getCoachInitials = profile => {
  if (!profile) return ''
  return `${profile.firstName?.substring(0, 1) ||
    ''}${profile.lastName?.substring(0, 1) || ''}`.toUpperCase()
}

const CoachReviews = props => {
  const { coachProfile, coachReviews, open, setOpen } = props
  const styles = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const loadMoreRef = useRef(null)

  const reviews = coachReviews.coachReviews.filter(
    ({ status }) => status === 'COMPLETE',
  )
  const [visibleCount, setVisibleCount] = useState(REVIEWS_BATCH_SIZE)
  const visibleReviews = reviews.slice(0, visibleCount)
  const hasMore = visibleCount < reviews.length

  const isLoadMoreVisible = useIntersectionObserver(loadMoreRef, {
    root: null,
    rootMargin: '200px',
    threshold: 0,
  })

  useEffect(() => {
    setVisibleCount(REVIEWS_BATCH_SIZE)
  }, [reviews.length])

  useEffect(() => {
    if (isLoadMoreVisible && hasMore) {
      setVisibleCount(current =>
        Math.min(current + REVIEWS_BATCH_SIZE, reviews.length),
      )
    }
  }, [isLoadMoreVisible, hasMore, reviews.length])

  const reviewLabel = reviews.length === 1 ? 'review' : 'reviews'

  return (
    <Box className={styles.card} data-testid="coach-reviews">
      <Box className={styles.cardBody}>
        <Box className={styles.requestBanner}>
          <Box className={styles.requestBannerCopy}>
            <AddCommentOutlinedIcon
              className={styles.requestBannerIcon}
              fontSize="small"
              opticalSize={20}
            />
            <Typography component="span" className={styles.requestBannerText}>
              Request reviews from your students to enhance your PGA Coach
              Profile.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            className={styles.requestBannerButton}
            onClick={() => setOpen(!open)}
          >
            Ask for reviews
          </Button>
        </Box>

        <Box className={styles.ratingSummary}>
          <Avatar
            aria-label="coach"
            src={secureLink(coachProfile?.profilePhoto)}
            className={styles.coachAvatar}
          >
            {!coachProfile?.profilePhoto && getCoachInitials(coachProfile)}
          </Avatar>
          <Box className={styles.ratingDetails}>
            <Typography className={styles.averageRating}>
              {coachReviews.averageRating.toFixed(2)}
            </Typography>
            <Rating
              name="coach-average-rating"
              value={coachReviews.averageRating}
              readOnly
              disableHover
              precision={0.5}
              className={styles.ratingStars}
              classes={{ iconButton: styles.ratingStarButton }}
              {...coachReviewCardRatingIcons}
            />
            <Typography className={styles.reviewCount}>
              Based on{' '}
              <Box component="span" className={styles.reviewCountNumber}>
                {reviews.length} {reviewLabel}
              </Box>{' '}
              from verified students
            </Typography>
          </Box>
        </Box>

        <Box>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>All reviews</Typography>
          </Box>

          <Box className={styles.reviewsList}>
            {visibleReviews.map(review => (
              <CoachReviewCard
                key={review.id}
                isMobile={isMobile}
                review={review}
              />
            ))}
            {hasMore && (
              <Box
                ref={loadMoreRef}
                className={styles.loadMoreSentinel}
                aria-hidden
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

CoachReviews.propTypes = {
  coachProfile: PropTypes.object,
  coachReviews: PropTypes.object,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
}

export default CoachReviews
